import { HStack, Stack, Text } from '@chakra-ui/react';
import { useLingui } from '@lingui/react/macro';
import MaximizeIcon from '~icons/lucide/maximize-2';
import VolumeXIcon from '~icons/lucide/volume-x';
import type { PointerEventHandler } from 'react';

import type { useCallRoom } from '#features/calls/hooks/useCallRoom';
import { MOBILE_BAR_BOTTOM, MOBILE_BAR_TOP, type MobileAnchor } from '#features/media-window/model';
import { Avatar } from '#features/ui/components/Avatar';
import { IconButton } from '#features/ui/components/IconButton';
import { floatingBarSurface } from '#features/ui/floatingBarSurface';
import { CallMicCluster } from './CallMicCluster';

/** Свёрнутый прямой звонок с теми же звуковыми действиями, что в голосе сессии. */
export const CallMini = ({
  peerName,
  peerAvatar,
  statusText,
  room,
  micEnabled,
  anchor,
  onToggleMic,
  onHangup,
  onExpand,
  onNodeChange,
  onGripPointerDown,
}: {
  peerName: string;
  peerAvatar: string | null;
  statusText: string;
  room: ReturnType<typeof useCallRoom>;
  micEnabled: boolean;
  anchor: MobileAnchor;
  onToggleMic: () => void;
  onHangup: () => void;
  onExpand: () => void;
  onNodeChange?: (node: HTMLDivElement | null) => void;
  onGripPointerDown?: PointerEventHandler<HTMLDivElement>;
}) => {
  const { t } = useLingui();
  return (
    <HStack
      ref={onNodeChange}
      data-voice-device-anchor=""
      data-anchor={anchor}
      {...floatingBarSurface}
      position="fixed"
      // Край на телефоне человек выбирает жестом. На десктопе пилюля стоит в правом низу, как
      // и окно. `auto` с противоположной стороны обязателен, иначе пилюля растягивается от верха до низа.
      top={{ base: anchor === 'top' ? MOBILE_BAR_TOP : 'auto', md: 'auto' }}
      bottom={{
        base: anchor === 'top' ? 'auto' : MOBILE_BAR_BOTTOM,
        md: 4,
      }}
      right={{ base: 3, md: 4 }}
      left={{ base: 3, md: 'auto' }}
      zIndex="modal"
      px={1.5}
      py={1}
      gap={1}
      onPointerDown={onGripPointerDown}
      css={{ touchAction: 'none', overscrollBehavior: 'contain' }}
      maxW={{ md: 'min(440px, calc(100vw - 32px))' }}
    >
      {/* Переносится вся полоса; жест сохраняет обычные нажатия на кнопки. */}
      <HStack
        gap={1}
        minW={0}
        flex={1}
        role="group"
        aria-label={peerName}
        title={peerName}
        userSelect="none"
      >
        <Avatar
          size="xs"
          src={peerAvatar}
          name={peerName}
          borderRadius="lg"
          css={{ '--avatar-radius': 'var(--chakra-radii-lg)' }}
        />
        <Stack
          gap={0}
          minW={0}
          flex={1}
          css={{
            '@media (max-width: 47.999rem)': { containerType: 'inline-size' },
            '& > p': {
              '@container (max-width: 32px)': { visibility: 'hidden' },
            },
          }}
        >
          <Text textStyle="label" lineClamp={1}>
            {peerName}
          </Text>
          <Text
            textStyle="caption"
            color="fg.muted"
            fontVariantNumeric="tabular-nums"
            lineClamp={1}
          >
            {statusText}
          </Text>
        </Stack>
      </HStack>
      {/* На телефоне пилюля — основной вид звонка, а плашка со звуком живёт в развёрнутом
              окне: без этой кнопки человек с заблокированным звуком не увидел бы вообще ничего и
              просто сидел бы в тишине. Появляется только когда звук правда заблокирован. */}
      {room.audioBlocked && (
        <IconButton
          aria-label={t`Turn on sound`}
          size="sm"
          minW="44px"
          h="44px"
          variant="solid"
          colorPalette="orange"
          borderRadius="xl"
          onClick={() => void room.unblockAudio()}
        >
          <VolumeXIcon />
        </IconButton>
      )}
      <CallMicCluster
        room={room}
        micEnabled={micEnabled}
        onToggleMic={onToggleMic}
        onHangup={onHangup}
      />
      <IconButton
        aria-label={t`Expand call`}
        size="sm"
        minW={{ base: '44px', md: '40px' }}
        h={{ base: '44px', md: '40px' }}
        variant="ghost"
        bg="bg.muted"
        color="fg.emphasized"
        flexShrink={0}
        css={{ '@media (any-pointer: coarse)': { minWidth: '44px', minHeight: '44px' } }}
        onClick={onExpand}
      >
        <MaximizeIcon />
      </IconButton>
    </HStack>
  );
};
